"use client";
import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect, FC } from "react";
import ClearDataButton from "components/Buttons/ClearDataButton";
import usePlacesAutocomplete, {
  getDetails,
  getGeocode,
} from "use-places-autocomplete";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
} from "@reach/combobox";
import { useDispatch } from "react-redux";
import { setCity, setCountry, setLatitude, setLongitude, setRegion, setBounds } from "application/features/search/searchSlice";
import { useTranslation } from "react-i18next";
import { APIProvider } from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY } from "data/constants";

export interface LocationSearchInputProps {
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  defaultValue?: string;
  onChange?: (value: string) => void;
  headingText?: string;
}

const LocationSearchInput: FC<LocationSearchInputProps> = ({
  autoFocus = false,
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  defaultValue = "",
  onChange = () => { },
  headingText = "",
}) => {

  const { t, ready } = useTranslation("translation");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPopover, setShowPopover] = useState(autoFocus);
  const dispatch = useDispatch();

  const {
    value,
    setValue,
    suggestions: { 
      // loading,
      status,
      data,
    },
    clearSuggestions,
    init
  } = usePlacesAutocomplete({
    requestOptions: {
      language: "it",
    },
    debounce: 300,
    cache: false,
    initOnMount: false,
  });

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    // CLICK IN_SIDE
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    // CLICK OUT_SIDE
    setShowPopover(false);
    setTimeout(() => {
        clearSuggestions();
    }, 100);
  };

  const handleSelectLocation = async (address: any) => {
    // DO NOT REMOVE SETTIMEOUT FUNC
    setTimeout(() => {
      setValue(address);
      onChange && onChange(address);
    }, 0);    const results = await getGeocode({ address });
    const details = await getDetails({ placeId: results[0].place_id, language: "it" });

    if (typeof details === "string") return;

    const latitude = details?.geometry?.location?.lat();
    const longitude = details?.geometry?.location?.lng();
    const viewport = details?.geometry?.viewport;
    let city, region, country;
    // if in address_components.types is not included "locality" then select region or country
    if (details.address_components && !details.address_components?.find((item: any) => item.types.includes("locality"))) {
      region = details?.address_components.find((item: any) => item.types.includes("administrative_area_level_1"))?.long_name;
      country = details?.address_components.find((item: any) => item.types.includes("country"))?.long_name;
    }

    city = details?.name;

    if (city !== region) {
      dispatch(setCity(city || ""));
    } else {
      dispatch(setCity(""));
    }
    dispatch(setCountry(country || ""));
    dispatch(setRegion(region || ""));
    dispatch(setLatitude(latitude || 0));
    dispatch(setLongitude(longitude || 0));
    if (viewport?.getNorthEast && viewport?.getSouthWest) {
      const northEast = viewport.getNorthEast();
      const southWest = viewport.getSouthWest();
      dispatch(setBounds({
        north: northEast.lat(),
        east: northEast.lng(),
        south: southWest.lat(),
        west: southWest.lng(),
      }));
    } else {
      dispatch(setBounds(null));
    }

    setShowPopover(false);
  };

  const renderSearchValue = () => {
    return (
      <>
        {
          status === "OK" && data.length === 0 && (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 px-4 sm:px-8">
              {t("translation:search.pages.fallback.noResults")}
            </div>
          )
        }
        {
          (status !== "OK" && status.length > 0) && (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 px-4 sm:px-8">
              {`Error: ${status}`}
            </div>
          )
        }
        {/* {
          loading && (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 px-4 sm:px-8">
              {t("translation:search.pages.fallback.loadingResults")}
            </div>
          )
        } */}
        {data.map(({ place_id, description }) => (
          <span
            onClick={handleSelectLocation.bind(null, description)}
            key={place_id}
            className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
          >
            <span className="block text-neutral-400">
              <MagnifyingGlassIcon className="h-4 w-4 sm:h-6 sm:w-6" />
            </span>
            <span className="block font-medium text-neutral-700 dark:text-neutral-200">
              <ComboboxOption key={place_id} value={description} style={{
                listStyle: "none",
              }} />
            </span>
          </span>
        ))}
      </>
    );
  };

  if (!ready) {
    return null; // or a loading spinner
  }

  return (
    <APIProvider 
      apiKey={GOOGLE_MAPS_API_KEY} 
      libraries={['places', 'geocoding']}
      authReferrerPolicy="origin"
      onError={(e) => console.error("Error loading Google Maps API", e)}
      onLoad={() => {
        console.log("Google Maps API loaded successfully");
        init();
      }}
    >
      <Combobox onSelect={handleSelectLocation}>
        <div className={`relative flex ${className}`} ref={containerRef}>
          <div
            onClick={() => setShowPopover(true)}
            className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${showPopover ? "nc-hero-field-focused" : ""
              }`}
          >
            <div className="text-neutral-300 dark:text-neutral-400">
              <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
            </div>
            <div className="flex-grow">
              <ComboboxInput
                onChange={(e) => setValue(e.target.value)}
                value={value}
                autoFocus={showPopover}
                ref={inputRef}
                className={`block w-full bg-transparent border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-neutral-300 xl:text-lg font-semibold placeholder-neutral-800 dark:placeholder-neutral-200 truncate`}
                placeholder={t("translation:search.common.destinationPlaceholder")}
              />
              <span className="block mt-0.5 text-sm text-neutral-400 font-light ">
                <span className="line-clamp-1">{!!value ? t("translation:search.common.destinationPlaceholder") : t("translation:search.common.destinationPlaceholder")}</span>
              </span>
              {value && showPopover && (
                <ClearDataButton
                  onClick={() => {
                    setValue("");
                  }}
                />
              )}
            </div>
          </div>

          {showPopover && (
            <div
              className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 bg-white dark:bg-neutral-800 ${divHideVerticalLineClass}`}
            ></div>
          )}

          {showPopover && value && (
            <div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-6 rounded-3xl shadow-xl max-h-96 overflow-y-auto">
              {renderSearchValue()}
            </div>
          )}
        </div>
      </Combobox>
    </APIProvider>
  );
};

export default LocationSearchInput;
